import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import MagicButton from 'components/MagicButton/MagicButton';
import { TopDeckedMark } from 'components/TopDeckedMark/TopDeckedMark';
import { useIsMobile } from 'hooks/useIsMobile';
import { useStartHaveListImportAsyncMutation, useStartWantListImportAsyncMutation } from 'store/services/import/importApi';
import {
  selectImportJob,
  selectImportProgress,
  selectIsImporting,
  importJobStarted,
  resetImportJob,
  setImportModalOpen,
  ImportSource,
} from 'store/slices/importSlice';
import './ImportCardsModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void;
  useOracleId?: boolean;
  isHaveList?: boolean;
  /** When true, skip input UI and show only progress/results (opened from pill) */
  statusMode?: boolean;
  /** Which source option opens preselected (the /topdecked deep link passes 'topdecked'). */
  initialInputMode?: ImportInputMode;
}

/**
 * The three importer sources. 'topdecked' is a preset over the same CSV upload path
 * (same dropzone, same POST, same results view); the only differences are the guidance
 * panel, the copy, and the `source` field stamped on the request.
 */
export type ImportInputMode = 'paste' | 'csv' | 'topdecked';
type InputMode = ImportInputMode;

const MODE_OPTIONS: { value: InputMode; label: string; tag?: string }[] = [
  { value: 'paste', label: 'Copy / Paste', tag: 'ctrl+v' },
  { value: 'csv', label: 'CSV Upload', tag: 'csv' },
  { value: 'topdecked', label: 'TopDecked' },
];

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export default function ImportCardsModal({ isOpen, onClose, onCardAdd, useOracleId = false, isHaveList = false, statusMode = false, initialInputMode = 'paste' }: Props) {
  const dispatch = useDispatch();
  const [inputMode, setInputMode] = useState<InputMode>(initialInputMode);
  // The modal goes full-screen at 640px; the TopDecked dropzone swaps to tap-first copy there.
  const isCompact = useIsMobile(640);
  const [pasteText, setPasteText] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [importForTrade, setImportForTrade] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A deep link can change the preselected source after mount (AddCards sets it, then opens).
  useEffect(() => {
    setInputMode(initialInputMode);
  }, [initialInputMode]);

  // Redux import state
  const job = useSelector((state: RootState) => selectImportJob(state));
  const progress = useSelector((state: RootState) => selectImportProgress(state));
  const isImportingAsync = useSelector((state: RootState) => selectIsImporting(state));

  // Async mutations
  const [startHaveListImport] = useStartHaveListImportAsyncMutation();
  const [startWantListImport] = useStartWantListImportAsyncMutation();

  // Determine if we're showing async progress (job is active or completed)
  const hasActiveJob = job.status !== 'idle';
  const showProgressView = statusMode || isImportingAsync || hasActiveJob;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error('File size must be less than 20MB');
          return;
        }
        setCsvFile(file);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setCsvFile(file);
    }
  }, []);

  const handleImport = async () => {
    setIsSubmitting(true);

    try {
      let body: FormData | { text: string; forTrade?: boolean };

      if (inputMode === 'paste') {
        if (!pasteText.trim()) {
          toast.error('Please enter card names to import');
          setIsSubmitting(false);
          return;
        }
        body = { text: pasteText, forTrade: isHaveList ? importForTrade : undefined };
      } else {
        if (!csvFile) {
          toast.error('Please select a CSV file to import');
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', csvFile);
        if (isHaveList) {
          formData.append('forTrade', importForTrade.toString());
        }
        // Attribution follows the user's choice, not format detection: only the TopDecked
        // option stamps source=topdecked. A TopDecked-format file uploaded through the
        // generic CSV option sends no source (backend records manual).
        if (inputMode === 'topdecked') {
          formData.append('source', 'topdecked');
        }
        body = formData;
      }
      const source: ImportSource = inputMode === 'topdecked' ? 'topdecked' : 'manual';

      // Use the async mutation for the appropriate list type
      const startImport = isHaveList ? startHaveListImport : startWantListImport;
      const response = await startImport(body).unwrap();

      // Response is { jobId, totalRows } from HTTP 202
      dispatch(importJobStarted({
        jobId: response.jobId,
        totalRows: response.totalRows,
        targetList: isHaveList ? 'have' : 'want',
        source,
      }));

      // Modal transitions to progress view via Redux state
    } catch (error: any) {
      console.error('Import error:', error);

      if (error?.status === 409 || error?.data?.error === 'IMPORT_ALREADY_ACTIVE') {
        toast.warning(error?.data?.message || 'You already have an import in progress.');
      } else if (error?.data?.message) {
        toast.error(error.data.message);
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        toast.error(`Import failed: ${error.data.errors[0]?.error || 'Unknown error'}`);
      } else {
        toast.error('An error occurred during import. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setPasteText('');
    setCsvFile(null);
    setImportForTrade(true);
    dispatch(resetImportJob());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (isImportingAsync) {
      // Import is running — just close the modal, pill will show progress
      dispatch(setImportModalOpen(false));
      onClose();
    } else {
      // No active import — full reset
      if (!hasActiveJob) {
        handleReset();
      }
      dispatch(setImportModalOpen(false));
      onClose();
    }
  };

  if (!isOpen) return null;

  // Render progress/results view when an async job is active or done
  const renderProgressView = () => {
    const isActive = job.status === 'pending' || job.status === 'processing';
    const isComplete = job.status === 'completed';
    const isFailed = job.status === 'failed';

    return (
      <>
        {/* Progress bar and live stats during import */}
        {isActive && (
          <div className="import-progress-section">
            <div className="import-progress-bar">
              <div
                className={`import-progress-fill ${progress.percentage === 0 ? 'import-progress-indeterminate' : ''}`}
                style={progress.percentage > 0 ? { width: `${progress.percentage}%` } : undefined}
              />
            </div>
            <p className="import-progress-text">
              {progress.percentage > 0
                ? `Processing row ${progress.processedRows.toLocaleString()} of ${progress.totalRows.toLocaleString()}`
                : 'Starting import...'}
            </p>
            <div className="import-progress-stats">
              <span className="import-stat-matched">{job.matched.toLocaleString()} matched</span>
              <span className="import-stat-skipped">{job.skipped.toLocaleString()} skipped</span>
              <span className="import-stat-failed">{job.failed.toLocaleString()} failed</span>
            </div>
            <p className="import-progress-hint">
              You can close this dialog — the import will continue in the background.
            </p>
          </div>
        )}

        {/* Completion summary */}
        {isComplete && (
          <div className="import-result-section">
            {/* TopDecked-sourced jobs state imported count, skipped count, and the
                condition default in one line (ticket 1215930107055664). The backend
                defaults NEAR_MINT per have-list row that carries no condition. */}
            {job.source === 'topdecked' && (
              <p className="import-result-line">
                {job.matched.toLocaleString()} {job.matched === 1 ? 'card' : 'cards'} added to your{' '}
                {job.targetList === 'want' ? 'Want List' : 'Have List'}. {job.skipped.toLocaleString()}{' '}
                {job.skipped === 1 ? 'row' : 'rows'} skipped.
                {job.targetList === 'have' ? ' Near Mint applied to rows without a condition.' : ''}
              </p>
            )}
            <div className="import-result-summary">
              <div className="import-result-stat success">
                <FontAwesomeIcon icon="check-circle" />
                <span>{job.matched.toLocaleString()} Imported</span>
              </div>
              {job.skipped > 0 && (
                <div className="import-result-stat skipped">
                  <FontAwesomeIcon icon="forward" />
                  <span>{job.skipped.toLocaleString()} Skipped</span>
                </div>
              )}
              {job.failed > 0 && (
                <div className="import-result-stat failed">
                  <FontAwesomeIcon icon="times-circle" />
                  <span>{job.failed.toLocaleString()} Failed</span>
                </div>
              )}
            </div>

            {/* Skip reasons breakdown */}
            {Object.keys(job.skipReasons).length > 0 && (
              <details className="import-failed-details">
                <summary>Skip reasons</summary>
                <div className="import-failed-list">
                  {Object.entries(job.skipReasons).map(([reason, count]) => (
                    <div key={reason} className="import-failed-item">
                      <span className="import-failed-name">{reason}</span>
                      <span className="import-failed-error">{count}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* D-7 bulk summary: rows the backend stored in a different finish than requested
                (a printing that exists in one finish only), with the card names. */}
            {job.finishAdjustments && job.finishAdjustments.count > 0 && (
              <details className="import-failed-details import-finish-adjustments">
                <summary>
                  {job.finishAdjustments.count.toLocaleString()} {job.finishAdjustments.count === 1 ? 'card' : 'cards'} stored as a different finish than requested
                </summary>
                <div className="import-failed-list">
                  {job.finishAdjustments.cards.slice(0, 50).map((entry, index) => (
                    <div key={`${entry.cardName}-${entry.setCode ?? ''}-${index}`} className="import-failed-item">
                      <span className="import-failed-name">
                        {entry.cardName}{entry.setCode ? ` [${entry.setCode.toUpperCase()}]` : ''}
                      </span>
                      <span className="import-failed-error">
                        Requested {entry.requested}, stored as {entry.stored}
                        {entry.reason === 'ONLY_FINISH_AVAILABLE' ? ' (only finish this printing exists in)' : ''}
                      </span>
                    </div>
                  ))}
                  {job.finishAdjustments.cards.length > 50 && (
                    <div className="import-failed-item">
                      <span className="import-failed-name" style={{ color: '#999' }}>
                        ...and {job.finishAdjustments.cards.length - 50} more
                      </span>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Failed cards detail */}
            {job.failedCards.length > 0 && (
              <details className="import-failed-details">
                <summary>View failed cards ({job.failedCards.length})</summary>
                <div className="import-failed-list">
                  {job.failedCards.slice(0, 50).map((card, index) => (
                    <div key={index} className="import-failed-item">
                      <span className="import-failed-name">{card.cardName}</span>
                      <span className="import-failed-error">{card.error}</span>
                    </div>
                  ))}
                  {job.failedCards.length > 50 && (
                    <div className="import-failed-item">
                      <span className="import-failed-name" style={{ color: '#999' }}>
                        ...and {job.failedCards.length - 50} more
                      </span>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Failure state */}
        {isFailed && (
          <div className="import-result-section">
            <div className="import-result-pending" style={{ background: 'rgba(248, 113, 113, 0.1)', color: '#f87171' }}>
              <FontAwesomeIcon icon="times-circle" style={{ fontSize: 24 }} />
              <span>Import Failed</span>
              {job.error && (
                <p className="import-pending-hint">{job.error}</p>
              )}
            </div>

            {/* Show partial results if any cards were processed */}
            {job.matched > 0 && (
              <div className="import-result-summary" style={{ marginTop: 16 }}>
                <div className="import-result-stat success">
                  <FontAwesomeIcon icon="check-circle" />
                  <span>{job.matched.toLocaleString()} Imported before failure</span>
                </div>
              </div>
            )}

            {job.failedCards.length > 0 && (
              <details className="import-failed-details">
                <summary>View failed cards ({job.failedCards.length})</summary>
                <div className="import-failed-list">
                  {job.failedCards.slice(0, 50).map((card, index) => (
                    <div key={index} className="import-failed-item">
                      <span className="import-failed-name">{card.cardName}</span>
                      <span className="import-failed-error">{card.error}</span>
                    </div>
                  ))}
                  {job.failedCards.length > 50 && (
                    <div className="import-failed-item">
                      <span className="import-failed-name" style={{ color: '#999' }}>
                        ...and {job.failedCards.length - 50} more
                      </span>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </>
    );
  };

  const selectMode = (mode: InputMode) => {
    setInputMode(mode);
    setPasteText('');
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Arrow keys move between cells like a native radio group.
  const handleModeKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const index = MODE_OPTIONS.findIndex((o) => o.value === inputMode);
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = MODE_OPTIONS[(index + delta + MODE_OPTIONS.length) % MODE_OPTIONS.length];
    selectMode(next.value);
    const cell = e.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-mode="${next.value}"]`);
    cell?.focus();
  };

  const clearFile = () => {
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // One dropzone for both CSV-backed sources. The TopDecked variant changes copy and the
  // selected-file row only; the input, handlers, size cap and POST are identical.
  const renderDropzone = (variant: 'csv' | 'topdecked') => (
    <div
      className={`import-dropzone ${variant === 'topdecked' ? 'import-dropzone--td' : ''} ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={isSubmitting}
      />

      {csvFile ? (
        variant === 'topdecked' ? (
          <div className="import-td-file">
            <TopDeckedMark size={22} />
            <div className="import-td-file-text">
              <span className="import-td-file-name">{csvFile.name}</span>
              <span className="import-td-file-meta">{Math.max(1, Math.round(csvFile.size / 1024))}KB</span>
            </div>
            <button
              type="button"
              className="import-td-file-replace"
              onClick={() => {
                clearFile();
                fileInputRef.current?.click();
              }}
              disabled={isSubmitting}
            >
              Replace
            </button>
          </div>
        ) : (
          <div className="import-file-info">
            <FontAwesomeIcon icon="file-csv" size="2x" />
            <p>{csvFile.name}</p>
            <p className="import-file-size">
              {(csvFile.size / 1024).toFixed(1)} KB
            </p>
            <button
              className="import-file-remove"
              onClick={clearFile}
              disabled={isSubmitting}
            >
              Remove
            </button>
          </div>
        )
      ) : variant === 'topdecked' ? (
        <>
          <div className="import-td-drop-icon" aria-hidden="true">
            <FontAwesomeIcon icon="file-csv" />
          </div>
          <p className="import-td-drop-title">
            {isCompact ? 'Choose a CSV file' : 'Drop your TopDecked CSV here'}
          </p>
          <p className="import-td-drop-sub">
            {isCompact ? (
              'from Files or iCloud Drive'
            ) : (
              <>
                or{' '}
                <button
                  type="button"
                  className="import-td-browse"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  browse your files
                </button>
                , .csv up to 20MB
              </>
            )}
          </p>
          {isCompact && (
            <button
              className="import-browse-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
            >
              Choose File
            </button>
          )}
        </>
      ) : (
        <>
          <FontAwesomeIcon icon="cloud-upload-alt" size="3x" />
          <p>Drop CSV file here or</p>
          <button
            className="import-browse-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            Choose File
          </button>
          <p className="import-format-hint">
            Max file size: 20MB<br />
            Format: {isHaveList ? (
              <>
                <span style={{ color: '#F59E0B', fontWeight: 600 }}>Set (required)</span>, Card Name, Quantity, Language, Condition, Foil
              </>
            ) : (
              'Card Name, Quantity, Set, Language, Condition, Foil'
            )}
          </p>
          <p className="import-format-hint">
            Finish column accepts Foil, Nonfoil, or Etched.
          </p>
        </>
      )}
    </div>
  );

  // Render input UI (source picker + paste/CSV/TopDecked)
  const renderInputView = () => (
    <>
      <div className="import-mode-selector">
        <div className="import-mode-segmented" role="radiogroup" aria-label="Input method">
          {MODE_OPTIONS.map((opt) => {
            const active = inputMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={active ? 0 : -1}
                data-mode={opt.value}
                className={`import-mode-cell ${active ? 'active' : ''}`}
                onClick={() => selectMode(opt.value)}
                onKeyDown={handleModeKeyDown}
                disabled={isSubmitting}
              >
                {opt.value === 'topdecked' && <TopDeckedMark size={17} className="import-mode-mark" />}
                <span className="import-mode-label">{opt.label}</span>
                {opt.tag && !active && <span className="import-mode-tag">{opt.tag}</span>}
                {opt.value === 'topdecked' && <span className="import-mode-new">NEW</span>}
              </button>
            );
          })}
        </div>
      </div>

      {isHaveList && (
        <div className="import-for-trade-option">
          <div className="import-for-trade-label">
            <button
              type="button"
              className={`trade-status-toggle ${importForTrade ? 'active' : ''}`}
              onClick={() => setImportForTrade(!importForTrade)}
              title={importForTrade ? 'For Trade' : 'Not for Trade'}
            >
              <FontAwesomeIcon icon={importForTrade ? 'right-left' : 'lock'} fontSize={12} />
            </button>
            <span>Add as: {importForTrade ? 'For Trade' : 'Not for Trade'}</span>
          </div>
          <span className="import-for-trade-hint">
            {importForTrade ? 'Cards will be available for auto-match' : 'Cards will be kept in your collection only'}
          </span>
        </div>
      )}

      {inputMode === 'paste' && (
        <div className="import-paste-section">
          <textarea
            className="import-paste-textarea"
            placeholder={isHaveList ? `Enter card names with sets, one per line:

2 Lightning Bolt [LEA]
Black Lotus [LEA]
4x Counterspell [LEA] NM

Set code is REQUIRED for Have List:
• Use [SET] after card name
• Example: Lightning Bolt [LEA]
• Defaults: Qty=1, Lang=EN, Cond=NM, Finish=Nonfoil
• Finish column accepts Foil, Nonfoil, or Etched` : `Enter card names, one per line:

Lightning Bolt
4 Counterspell
2x Brainstorm

Supported formats:
• Card Name (defaults to 1)
• 4 Card Name
• 4x Card Name`}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      )}

      {inputMode === 'csv' && renderDropzone('csv')}

      {inputMode === 'topdecked' && (
        <div className="import-td-panel">
          <div className="import-td-head">
            <TopDeckedMark size={44} />
            <div className="import-td-head-text">
              <span className="import-td-title">Import your TopDecked export</span>
              <span className="import-td-sub">Three steps. We read TopDecked&apos;s CSV format directly.</span>
            </div>
          </div>
          <ol className="import-td-steps">
            <li className="import-td-step">
              <span className="import-td-step-badge" aria-hidden="true">1</span>
              <span className="import-td-step-copy">
                In TopDecked, open a list in <strong>My Cards</strong>, tap <strong>Share</strong>, and choose <strong>Download CSV</strong>.
              </span>
            </li>
            <li className="import-td-step">
              <span className="import-td-step-badge" aria-hidden="true">2</span>
              <span className="import-td-step-copy">Save the CSV file to your device.</span>
            </li>
            <li className="import-td-step">
              <span className="import-td-step-badge" aria-hidden="true">3</span>
              <span className="import-td-step-copy">Upload it here.</span>
            </li>
          </ol>
          <p className="import-td-note">
            Foils and etched cards import as separate entries. You can edit conditions after import.
          </p>
          <p className="import-td-note">Importing the same file twice adds those cards again.</p>
          {renderDropzone('topdecked')}
        </div>
      )}
    </>
  );

  const isActive = job.status === 'pending' || job.status === 'processing';
  const isDone = job.status === 'completed' || job.status === 'failed';

  return createPortal(
    <div className="import-modal-overlay" onClick={handleClose}>
      <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>
            {showProgressView
              ? (isActive ? 'Importing Cards...' : isDone ? 'Import Results' : 'Import Cards')
              : `Import Cards to ${isHaveList ? 'Have List' : 'Want List'}`
            }
          </h2>
          <button className="import-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <div className="import-modal-body">
          {showProgressView ? renderProgressView() : renderInputView()}
        </div>

        <div className="import-modal-footer">
          {isActive ? (
            <MagicButton
              label="Minimize"
              color="grey"
              onPress={handleClose}
            />
          ) : isDone ? (
            <>
              <MagicButton
                label="Done"
                color="grey"
                onPress={() => {
                  handleReset();
                  handleClose();
                }}
              />
              <MagicButton
                label="Import More"
                color="purple"
                onPress={handleReset}
              />
            </>
          ) : (
            <>
              <MagicButton
                label="Cancel"
                color="grey"
                onPress={handleClose}
                isDisabled={isSubmitting}
              />
              <MagicButton
                label={isSubmitting ? 'Starting...' : 'Import'}
                color="purple"
                onPress={handleImport}
                isDisabled={isSubmitting || (inputMode === 'paste' ? !pasteText.trim() : !csvFile)}
                isLoading={isSubmitting}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
